import { useCeleryWorkflowStatusesRetrieve } from "gen/endpoints/celery-workflow-statuses/celery-workflow-statuses";
import { useFundFamilySubEventListSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { CeleryWorkflowStatus } from "gen/model/celeryWorkflowStatus";
import { noop, throttle } from "lodash-es";
import { useCallback, useEffect, useMemo } from "react";
import { useMapState } from "shared/hooks/useMapState";

/**
 * Hook to poll celery workflows and show toasts based on the workflow state.
 * @param celeryWorkflowIds List of celery workflow ids to poll
 * @param renderToasts Function to render toasts based on the workflow state
 * @param onSuccess Function to call when the workflow is complete
 * @param onError Function to call when the workflow has failed
 * @param updateConfig Optional config to handle updates when a workflow is processing
 * @param updateConfig.onProcessing Function to call when a workflow is processing
 * @param updateConfig.allowedInterval Allowed interval between processing updates. E.g. if 5000ms is passed in here, the onProcessing function will be called at most once every 5 seconds.
 * @param forceOnSuccess If true, the onSuccess function will be called even if the workflow state has not changed. This is useful when you want to force a refresh of the page when a workflow is complete.
 * @returns Map of celery workflow ids to their status
 */
export const useCeleryWorkflowPoll = (
  celeryWorkflowIds: string[] | undefined,
  renderToasts: (is_failed: boolean) => void,
  onSuccess: () => void,
  onError: (error: Error) => void,
  updateConfig?: {
    onProcessing: () => void;
    allowedInterval: number;
  },
  forceOnSuccess: boolean = false,
): Map<string, CeleryWorkflowStatus> => {
  const {
    get: getCachedState,
    set: setCachedState,
    has: hasCachedState,
    getFullMap: getCache,
  } = useMapState<string, CeleryWorkflowStatus>();

  const throttledUpdate = useMemo(() => {
    const throttled = throttle(() => {
      updateConfig?.onProcessing();
    }, updateConfig?.allowedInterval);
    return throttled;
  }, [updateConfig]);

  const { data: workflowStatusList } = useCeleryWorkflowStatusesRetrieve(
    {
      id_list: celeryWorkflowIds ?? [],
    },
    {
      query: {
        // enable only if there is a celery workflow that has not been seen yet or that is not in a terminal state
        enabled:
          celeryWorkflowIds?.some(
            (celeryWorkflowId: string) =>
              !hasCachedState(celeryWorkflowId) ||
              getCachedState(celeryWorkflowId)?.state ===
                CeleryWorkflowStateMachineFieldsEnum.draft ||
              getCachedState(celeryWorkflowId)?.state ===
                CeleryWorkflowStateMachineFieldsEnum.processing,
          ) ?? false,
        refetchInterval: 1000, // Poll every second,
        structuralSharing: !updateConfig,
        // Structural sharing is disabled if we have an updateConfig because we intentionally want to trigger a rerender
        // even if the shape of the response has not changed (which is common when we are polling a workflow with the same state)
      },
    },
  );

  useEffect(() => {
    const anyWorkFlowIsProcessing = workflowStatusList?.some(
      (workflowStatus) =>
        workflowStatus.state ===
        CeleryWorkflowStateMachineFieldsEnum.processing,
    );
    if (anyWorkFlowIsProcessing) {
      throttledUpdate();
    }
  }, [workflowStatusList, throttledUpdate]);
  useEffect(() => {
    workflowStatusList?.forEach((workflowStatus) => {
      if (!hasCachedState(workflowStatus.id)) {
        // If we have not seen the workflow before, cache it and don't do anything
        // It means it's either a new workflow or we are re-loading the page. Either way, we don't want to show any toast or refresh the page.
        if (
          forceOnSuccess &&
          workflowStatus.state === CeleryWorkflowStateMachineFieldsEnum.complete
        ) {
          onSuccess();
        }
        setCachedState(workflowStatus.id, workflowStatus);
      } else {
        const cached_result = getCachedState(workflowStatus.id);
        if (cached_result != workflowStatus) {
          setCachedState(workflowStatus.id, workflowStatus);
          // Handle new state change

          if (
            workflowStatus.state ==
            CeleryWorkflowStateMachineFieldsEnum.complete
          ) {
            renderToasts(false);
            onSuccess();
          } else if (
            workflowStatus.state == CeleryWorkflowStateMachineFieldsEnum.failed
          ) {
            renderToasts(true);
            onError(new Error(workflowStatus.error_message));
          }
        }
      }
    });
  }, [
    hasCachedState,
    setCachedState,
    getCachedState,
    renderToasts,
    onSuccess,
    onError,
    workflowStatusList,
    forceOnSuccess,
  ]);
  return getCache();
};

export const useCeleryWorkflowPollSimple = ({
  forceOnSuccess = false,
  celeryWorkflowIds,
  renderToasts = noop,
  onSuccess = noop,
  onError = noop,
  updateConfig,
}: {
  celeryWorkflowIds: string[] | undefined;
  renderToasts?: (is_failed: boolean) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  updateConfig?: {
    onProcessing: () => void;
    allowedInterval: number;
  };
  forceOnSuccess?: boolean;
}) => {
  const cache = useCeleryWorkflowPoll(
    celeryWorkflowIds,
    renderToasts,
    onSuccess,
    onError,
    updateConfig,
    forceOnSuccess,
  );

  return {
    cache,
    areAnyProcessing: Array.from(cache.values()).some(
      (workflowStatus) =>
        workflowStatus.state ===
        CeleryWorkflowStateMachineFieldsEnum.processing,
    ),
  };
};

export const useRefreshOnSubeventsComplete = ({
  fundFamilyId,
  fundFamilyEventId,
  refetch,
}: {
  fundFamilyId: string;
  fundFamilyEventId: string;
  refetch: () => Promise<void>;
}) => {
  const onWorkflowsComplete = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const { data: subevents } = useFundFamilySubEventListSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const subEventCeleryWorkflowIds = subevents.flatMap((subevent) =>
    subevent.celery_workflow_id ? [subevent.celery_workflow_id] : [],
  );

  const { areAnyProcessing, cache } = useCeleryWorkflowPollSimple({
    celeryWorkflowIds: subEventCeleryWorkflowIds,
    onSuccess: onWorkflowsComplete,
    forceOnSuccess: true,
  });

  return {
    areAnyProcessing,
    cache,
  };
};
